import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Search, Building2, Users, Film, Settings } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { API_BASE } from '../../config/api';

const AdminWorkspaces = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [overrideFilter, setOverrideFilter] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchWorkspaces = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (planFilter) params.append('plan_id', planFilter);
      if (overrideFilter) params.append('has_overrides', overrideFilter);
      
      const response = await fetch(
        `${API_BASE}/admin/support/workspaces/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Search failed');
      }
      
      const data = await response.json();
      setSearchResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchWorkspaces();
    }
  };

  const getPlanBadgeVariant = (planId) => {
    switch (planId) {
      case 'plan_studio_v2':
      case 'plan_studio':
        return 'default';
      case 'plan_pro_v2':
      case 'plan_pro':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getPlanName = (planId) => {
    switch (planId) {
      case 'plan_studio_v2':
      case 'plan_studio':
        return 'Studio';
      case 'plan_pro_v2':
        return 'Director (Pro)';
      case 'plan_pro':
        return 'Pro';
      default:
        return 'Free';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Workspaces</h2>
        <p className="text-muted-foreground">Search and manage workspace settings</p>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, owner email, or workspace ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-10"
              />
            </div>
            
            <Select value={planFilter || "all"} onValueChange={(v) => setPlanFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Plans" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="plan_free">Free</SelectItem>
                <SelectItem value="plan_pro_v2">Director (Pro)</SelectItem>
                <SelectItem value="plan_studio_v2">Studio</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={overrideFilter || "all"} onValueChange={(v) => setOverrideFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Override Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Workspaces</SelectItem>
                <SelectItem value="true">Has Overrides</SelectItem>
                <SelectItem value="false">No Overrides</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={searchWorkspaces} disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Search Results ({searchResults.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {searchResults.map((workspace) => (
                <div
                  key={workspace.id}
                  className="flex items-center justify-between p-4 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors"
                  onClick={() => navigate(`/admin/support/workspaces/${workspace.id}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        {workspace.name}
                        <Badge variant={getPlanBadgeVariant(workspace.plan_id)}>
                          {getPlanName(workspace.plan_id)}
                        </Badge>
                        {workspace.has_overrides && (
                          <Badge variant="outline" className="text-xs">
                            <Settings className="h-3 w-3 mr-1" />
                            Override
                          </Badge>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Owner: {workspace.owner_name || workspace.owner_email || workspace.owner_id}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {workspace.member_count}
                    </div>
                    <div className="flex items-center gap-1">
                      <Film className="h-4 w-4" />
                      {workspace.film_count}
                    </div>
                    <div>
                      {new Date(workspace.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {searchResults.length === 0 && !loading && searchQuery && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No workspaces found. Try a different search term.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminWorkspaces;
